import { z } from "zod";

// Schema for fuzzy dates used in medication intervals
const fuzzyDateSchema = z.object({
  year: z.number().optional(),
  month: z.number().optional(),
  day: z.number().optional(),
  current: z.boolean().optional(),
  isBeforeX: z.boolean().optional(),
});

// Schema for medication intervals
const medicationIntervalSchema = z.object({
  start: fuzzyDateSchema,
  end: fuzzyDateSchema,
});

// Schema for medication value
const medicationValueSchema = z.object({
  hasTaken: z.boolean(),
  intervals: z.array(medicationIntervalSchema).optional(),
});

// Schema for medication group value
const medicationGroupValueSchema = z.object({
  hasTaken: z.boolean(),
  intervals: z.array(medicationIntervalSchema).optional(),
});

// Base schema for a single attribute value
const attributeValueInputSchema = z.object({
  attributeDefinitionId: z.number(),
  isSkipped: z.boolean().optional(),
  booleanAttributeValue: z.boolean().optional(),
  floatAttributeValue: z.number().optional(),
  radioAttributeValue: z.string().optional(),
  medicationAttributeValue: medicationValueSchema.optional(),
  medicationGroupAttributeValue: medicationGroupValueSchema.optional(),
});

// Schema for an array of attribute values, which is what mappers return
export const attributeValueInputArraySchema = z.array(attributeValueInputSchema);

/**
 * Schema for the `valueMapper` configuration.
 * It expects an object where keys are strings (the response values)
 * and values are an array of attribute definitions to set.
 */
export const valueMapperConfigSchema = z.record(
  z.string(),
  attributeValueInputArraySchema
);

/**
 * Schema for the `heightMapper` and `weightMapper` configuration.
 */
export const measurementMapperConfigSchema = z.object({
  attributeDefinitionId: z.number(),
});

/**
 * Schema for the `medicationGridMapper` configuration.
 * It expects an object with a `medicationMap` where keys are medication names (strings)
 * and values are their corresponding attribute definition IDs (numbers).
 */
export const medicationGridMapperConfigSchema = z.object({
  medicationMap: z.record(z.string(), z.number()),
});

/**
 * Schema for the JSON-stringified response from a medication grid question.
 * The response is expected to be a JSON string that parses into an object
 * with string keys and string values (e.g., `{ "0": "semaglutide_current" }`).
 */
export const medicationGridResponseSchema = z.record(z.string(), z.string());

/**
 * Schema for the `aiMedicationMapper` configuration.
 * It expects an object with a `categoryAttributeMap` that maps medication categories
 * to their attribute IDs, supporting both specific medications and "all" mappings.
 */
export const aiMedicationMapperConfigSchema = z.object({
  categoryAttributeMap: z.record(
    z.string(),
    z.union([
      z.record(z.string(), z.number()),
      z.object({ all: z.number() })
    ])
  ),
});

// Export the medication-related schemas for use in other modules
export {
  fuzzyDateSchema,
  medicationIntervalSchema,
  medicationValueSchema,
  medicationGroupValueSchema,
  attributeValueInputSchema,
}; 